import React, { useEffect, useState } from "react";
import { IconSearch } from "@tabler/icons-react";
import { LangConstant, SystemConstant } from "const";
import { useTranslation } from "react-i18next";
import { Pagination } from "components";
import { useAddMember, useDeleteMember, useRole } from "hooks";

const AddMemberDialog = ({
  open,
  selectedDepartment,
  unitList,
  totalPages,
  currentPageUsers,
  page,
  departmentUserList,
  onFilter,
  onClose,
}) => {
  const { t: getLabel } = useTranslation();
  const userDetail = getLabel(LangConstant.OBJ_USER_DETAIL, { returnObjects: true });

  const { addMemberAsync } = useAddMember();
  const { deleteMemberAsync } = useDeleteMember();
  const { isServiceAdmin } = useRole();

  const [checkedList, setCheckedList] = useState([]);
  const [defaultUserList, setDefaultUserList] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState(selectAllUnitValue);
  const [searchValue, setSearchValue] = useState("");

  const handleChangeSearch = event => {
    setSearchValue(event.target.value);
    onFilter({
      searchValue: event.target.value,
      page: SystemConstant.ACTIVE_PAGINATION,
    });
  };

  const handleChangeUnit = event => {
    setSelectedUnitId(event.target.value);
    onFilter({
      selectedUnitId: event.target.value,
      page: SystemConstant.ACTIVE_PAGINATION,
    });
  };

  const handleChangePage = (newPage) => {
    onFilter({ page: newPage });
  };

  const handleChangeCheckedList = userId => {
    if (checkedList.includes(userId)) {
      setCheckedList(currentState => currentState.filter(id => id !== userId));
    } else {
      setCheckedList(currentState => [...currentState, userId]);
    }
  };

  const handleAddMemberList = async () => {
    const addUserList = checkedList.filter(userId => !defaultUserList.includes(userId));
    if (addUserList.length > 0) {
      await addMemberAsync({
        departmentId: selectedDepartment.departmentId,
        userIds: addUserList,
        unitId: selectedDepartment.unitId,
        onClose: onClose,
      });
    }

    const removeUserList = defaultUserList.filter(userId => !checkedList.includes(userId));
    if (removeUserList.length > 0) {
      await deleteMemberAsync({
        departmentId: selectedDepartment.departmentId,
        userIds: removeUserList,
        unitId: selectedDepartment.unitId,
        onClose: onClose,
      });
    }
  };

  useEffect(() => {
    if (open) {
      const departmentUserIdList = departmentUserList.map(user => user.id);
      setCheckedList(departmentUserIdList);
      setDefaultUserList(departmentUserIdList);
      setSelectedUnitId(selectAllUnitValue);
      setSearchValue("");
      onFilter({
        selectedUnitId: selectAllUnitValue,
        searchValue: "",
        page: SystemConstant.ACTIVE_PAGINATION,
      });
    }
  }, [open]);

  // ESC key handler
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = e => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
      <div className="modal show d-block" tabIndex="-1" style={{ zIndex: 1050 }}>
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content" style={{ maxHeight: "80vh" }}>
            <div className="modal-header">
              <h5 className="modal-title" style={{ fontWeight: 600 }}>
                {getLabel(LangConstant.TXT_ADD_MEMBER_DIALOG)}
              </h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
            </div>
            <div className="modal-body" style={{ overflowY: "auto" }}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="input-group" style={{ margin: "10px 10px 10px 0", width: "50%" }}>
                  <span className="input-group-text">
                    <IconSearch size={16} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    value={searchValue}
                    onChange={handleChangeSearch}
                    placeholder={getLabel(
                      isServiceAdmin
                        ? LangConstant.P_SEARCH_USER_BY_NAME_PHONE
                        : LangConstant.P_SEARCH_USER_BY_NAME_PHONE_EMAIL,
                    )}
                  />
                </div>
                <div style={{ margin: "10px 0 10px 10px", width: "50%" }}>
                  <select
                    className="form-select"
                    value={selectedUnitId}
                    onChange={handleChangeUnit}
                  >
                    <option value={selectAllUnitValue}>{getLabel(LangConstant.L_ALL)}</option>
                    {unitList && unitList.length > 0 &&
                      unitList.map((unit, index) => (
                        <option key={index} value={unit.unitId}>
                          {unit.unitName}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <table className="table table-vcenter" style={{ whiteSpace: "nowrap", tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "5%" }}></th>
                      <th style={{ width: "20%", fontWeight: "bold" }}>{userDetail.userName}</th>
                      <th style={{ width: "15%", fontWeight: "bold" }}>{userDetail.phone}</th>
                      {!isServiceAdmin && (
                        <th style={{ width: "20%", fontWeight: "bold" }}>{userDetail.email}</th>
                      )}
                      <th style={{ width: isServiceAdmin ? "30%" : "20%", fontWeight: "bold" }}>
                        {userDetail.unit}
                      </th>
                      <th style={{ width: isServiceAdmin ? "30%" : "20%", fontWeight: "bold" }}>
                        {userDetail.department}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageUsers && currentPageUsers.length > 0 ? (
                      currentPageUsers.map((row, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={checkedList.includes(row.id)}
                              onChange={() => handleChangeCheckedList(row.id)}
                            />
                          </td>
                          <td
                            title={row.name}
                            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            {row.name}
                          </td>
                          <td
                            title={row.phone}
                            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            {row.phone}
                          </td>
                          {!isServiceAdmin && (
                            <td
                              title={row.mail}
                              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                            >
                              {row.mail}
                            </td>
                          )}
                          <td
                            title={row.unit}
                            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            {row.unit}
                          </td>
                          <td
                            title={row.department}
                            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            {row.department}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={isServiceAdmin ? 5 : 6} style={{ textAlign: "center" }}>
                          <small>
                            <i>{getLabel(LangConstant.TXT_NOT_FOUND)}</i>
                          </small>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={(e, p) => handleChangePage(p)} />
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline-danger"
                style={{ width: 150 }}
                onClick={onClose}
              >
                {getLabel(LangConstant.L_CANCEL)}
              </button>
              <button
                className="btn btn-primary"
                style={{ width: 150 }}
                onClick={handleAddMemberList}
              >
                {getLabel(LangConstant.L_CONFIRM_BUTTON)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddMemberDialog;

export const selectAllUnitValue = "*";
